import '@/styles/globals.css'
import '@/styles/hire.css'
import '@/styles/seeker.css'

import type { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { MediaContextProvider } from '@/commonComponents/Media'
import ErrorBoundary from '@/commonComponents/ErrorBoundary'
import { CookieWrapper } from '@/commonComponents/CookieWrapper'

import { LOADER_EXCLUDE } from '@/constants/all'
import { SharedContextProvider } from '@/context/SharedContext'
import { AdvertiserContextProvider } from '@/context/AdvertiserContext'
import { SeekerContextProvider } from '@/context/SeekerContext'

import bgImg from '../../public/assets/background10.webp'
import { bootstrap, setAnalyticsIdentity } from '@/analytics'

type LoaderState = { isVisible: boolean; pathName: string }

export default function App({ Component, pageProps }: AppProps) {
  /* ── derived flags ──────────────────────────────── */
  const router = useRouter()
  const isHire = /^(?:\/hire|\/recruit)(?:[\/?#].*)?$/.test(router.asPath)
  const isSeekerPage = router.asPath.includes('seeker_profile')

  const GA_ID = useMemo(
    () => (isHire ? process.env.NEXT_PUBLIC_GTAG_ID : process.env.NEXT_PUBLIC_SEEKERS_GTAG_ID),
    [isHire]
  )

  const FB_ID = useMemo(
    () => (isHire ? process.env.NEXT_PUBLIC_FB_ID : process.env.NEXT_PUBLIC_FB_ID_SEEKER),
    [isHire]
  )

  /* ── skeleton loader state ──────────────────────── */
  const [loader, setLoader] = useState<LoaderState>({ isVisible: false, pathName: '' })

  const onRouteStart = useCallback((url: string) => {
    if (!LOADER_EXCLUDE.includes(url)) setLoader({ isVisible: true, pathName: 'defaultLoader' })
  }, [])

  const onRouteFinish = useCallback(() => {
    setLoader({ isVisible: false, pathName: 'defaultLoader' })
  }, [])

  /* ── side-effects ───────────────────────────────── */
  /** attach / detach router listeners */
  useEffect(() => {
    router.events.on('routeChangeStart', onRouteStart)
    router.events.on('routeChangeComplete', onRouteFinish)
    router.events.on('routeChangeError', onRouteFinish)
    return () => {
      router.events.off('routeChangeStart', onRouteStart)
      router.events.off('routeChangeComplete', onRouteFinish)
      router.events.off('routeChangeError', onRouteFinish)
    }
  }, [router.events, onRouteStart, onRouteFinish])

  /** lazy-load AntD reset once */
  useEffect(() => {
    //@ts-ignore
    import('antd/dist/reset.css')
  }, [])

  /** initialise analytics SDKs */
  useEffect(() => {
    bootstrap({ gaId: GA_ID, fbPixelId: FB_ID })
  }, [GA_ID, FB_ID])

  /** restore persisted analytics identity */
  useAnalyticsIdentity()

  /* ── render ─────────────────────────────────────── */
  return (
    <>
      <GlobalHead isHire={isHire} asPath={router.asPath} />

      <MediaContextProvider>
        <ErrorBoundary>
          <CookieWrapper>
            {!isSeekerPage && <MoEngageScripts />}
            <SharedContextProvider skeletonloader={loader} setSkeletonloader={setLoader}>
              {isHire ? (
                <AdvertiserContextProvider>
                  <Component {...pageProps} />
                </AdvertiserContextProvider>
              ) : (
                <SeekerContextProvider>
                  <Component {...pageProps} />
                </SeekerContextProvider>
              )}
            </SharedContextProvider>
          </CookieWrapper>
        </ErrorBoundary>
      </MediaContextProvider>
    </>
  )
}

/* ────────────────────────────────
   Hooks / Helpers
────────────────────────────────── */

function useAnalyticsIdentity() {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const { jobsUserId, mobileNumber } = JSON.parse(localStorage.getItem('loginDetails') ?? '{}')
    if (jobsUserId) setAnalyticsIdentity({ jobsId: jobsUserId, mobile: mobileNumber })
  }, [])
}

/* ────────────────────────────────
   Head  (title / meta)
────────────────────────────────── */

interface HeadProps {
  isHire: boolean
  asPath: string
}

const GlobalHead: React.FC<HeadProps> = ({ isHire, asPath }) => (
  <Head>
    <title>
      {isHire
        ? 'Hire staff with a job post | Lokal Hiring Portal'
        : 'Job Openings & Vacancies - Find the Perfect Job'}
    </title>

    {/* perf */}
    <link rel="preload" href={bgImg.src} as="image" />
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    <link
      rel="stylesheet"
      href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@100;300;400;600;700&display=swap"
    />

    {/* SEO */}
    <link rel="canonical" href={`${process.env.NEXT_PUBLIC_BASE_URL}${asPath}`} />
    <meta property="og:site_name" content="Jobs Website | GetLokalApp" />
    <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_BASE_URL}${asPath}`} />
    <meta property="twitter:site" content="@getlokalapp" />
    <meta property="twitter:creator" content="@getlokalapp" />
  </Head>
)

/* ────────────────────────────────
   MoEngage loader
────────────────────────────────── */

const MoEngageScripts = () => (
  <>
    <Script
      id="moe-stub"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `(function(i,s,o,g,r,a,m,n){i.moengage_object=r;t={};q=function(f){return function(){(i.moengage_q=i.moengage_q||[]).push({f:f,a:arguments})}};f=['track_event','add_user_attribute','add_first_name','add_last_name','add_email','add_mobile','add_user_name','add_gender','add_birthday','destroy_session','add_unique_user_id','moe_events','call_web_push','track','location_type_attribute'],h={onsite:["getData","registerCallback"]};for(k in f){t[f[k]]=q(f[k])}for(k in h)for(l in h[k]){null==t[k]&&(t[k]={}),t[k][h[k][l]]=q(k+"."+h[k][l])}a=s.createElement(o);m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m);i.moe=i.moe||function(){n=arguments[0];return t};a.onload=function(){if(n){i[r]=moe(n)}}})(window,document,'script','https://cdn.moengage.com/webpush/moe_webSdk.min.latest.js','Moengage');`
      }}
    />
    <Script
      id="moe-init"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `Moengage = moe({
          app_id: '${process.env.NEXT_PUBLIC_MOENGAGE_APP_ID}',
          debug_logs: ${process.env.NEXT_PUBLIC_DEBUG_LOGS},
          enableSPA: true,
          cluster: "DC_3"
        });`
      }}
    />
  </>
)

/* ────────────────────────────────
   Tiny helpers exported for tests
────────────────────────────────── */

export const pageScripts = () => <></>
export const seekersPageScripts = () => <></>
